import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent } from "react";
import { createPortal } from "react-dom";
import { useSession } from "../hooks";
import { sortedMessages, sortedReplies } from "../sync/store";
import type { Attachment, Message } from "../sync/types";
import { inlineImages, type ViewImage } from "./Attachments";
import { IconClose } from "./icons";
import { locale, t } from "../i18n";

type Shown = { file: Attachment; message: Message };

// ImageHost opens the image viewer when an image in a message is clicked,
// with the other images of that channel (or thread) to step through.
export function ImageHost() {
  const session = useSession();
  const [open, setOpen] = useState<{ list: Shown[]; at: number } | null>(null);
  useEffect(() => {
    const on = (e: Event) => {
      const v = (e as CustomEvent<ViewImage>).detail;
      const st = session.store.getSnapshot();
      const cs = st.channels.get(v.channelID);
      let msgs: Message[] = [];
      if (v.threadRoot) {
        const root = cs?.messages.get(v.threadRoot);
        msgs = [...(root ? [root] : []), ...sortedReplies(st, v.threadRoot)];
      } else if (cs) msgs = sortedMessages(cs);
      const list = msgs.flatMap((m) => (m.files ?? []).filter((f) => inlineImages.has(f.mime)).map((file) => ({ file, message: m })));
      const at = list.findIndex((x) => x.file.id === v.fileID && x.message.id === v.messageID);
      if (at >= 0) setOpen({ list, at });
    };
    addEventListener("lantern:view-image", on);
    return () => removeEventListener("lantern:view-image", on);
  }, [session]);
  return open ? <ImageViewer list={open.list} start={open.at} onClose={() => setOpen(null)} /> : null;
}

// Zoom is the image's scale against its actual size: it opens fitted to the
// screen (never enlarged), and zooms out to a tenth of that or in to 8×, as
// long as the picture stays within maxPixels on its long side.
const step = 1.25;
const maxScale = 8;
const maxPixels = 16384;
// near is whether two scales are the same but for rounding.
const near = (a: number, b: number) => Math.abs(a - b) <= b * 1e-3;

function ImageViewer({ list, start, onClose }: { list: Shown[]; start: number; onClose: () => void }) {
  const session = useSession();
  const [at, setAt] = useState(start);
  const [scale, setScale] = useState<number | null>(null); // null fits the screen
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [room, setRoom] = useState<{ w: number; h: number } | null>(null);
  const [url, setURL] = useState<string | null>(null);
  const stage = useRef<HTMLDivElement>(null);
  // The point of the image to keep in place as the zoom changes: a fraction
  // of the image, and where it was in the stage.
  const anchor = useRef<{ fx: number; fy: number; x: number; y: number } | null>(null);
  const shown = list[at];

  useEffect(() => {
    let live = true;
    setURL(null);
    setScale(null);
    setNatural(null);
    anchor.current = null;
    session.fileURL(shown.file.id).then((u) => live && setURL(u), () => {});
    return () => {
      live = false;
    };
  }, [shown.file.id, session]);

  useLayoutEffect(() => {
    const el = stage.current;
    if (!el) return;
    const measure = () => {
      const cs = getComputedStyle(el);
      const pad = (a: string, b: string) => parseFloat(cs.getPropertyValue(a)) + parseFloat(cs.getPropertyValue(b));
      // Whole pixels down, so a fitted image never brings up a scroll bar.
      setRoom({ w: Math.floor(el.clientWidth - pad("padding-left", "padding-right")), h: Math.floor(el.clientHeight - pad("padding-top", "padding-bottom")) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = natural && room ? Math.min(1, room.w / natural.w, room.h / natural.h) : 1;
  const lowest = fit / 10;
  const highest = natural ? Math.max(fit, Math.min(maxScale, maxPixels / Math.max(natural.w, natural.h))) : maxScale;
  const shownScale = scale ?? fit;
  const size = natural ? { width: natural.w * shownScale, height: natural.h * shownScale } : null;
  // The scale as last asked for, ahead of the render: wheel events come
  // faster than renders, and each zooms from where the one before left it.
  const latest = useRef(shownScale);
  useLayoutEffect(() => {
    latest.current = shownScale;
  });

  // zoomTo changes the scale, keeping the image's point under (x, y) in the
  // stage where it is; the centre by default. A scale at the fit is the fit.
  const zoomTo = useCallback(
    (next: number | null, x?: number, y?: number) => {
      const to = next === null ? fit : Math.min(highest, Math.max(lowest, next));
      if (near(to, latest.current)) return;
      const el = stage.current;
      const img = el?.querySelector("img");
      if (el && img) {
        const r = img.getBoundingClientRect();
        const s = el.getBoundingClientRect();
        const px = x ?? s.left + el.clientWidth / 2;
        const py = y ?? s.top + el.clientHeight / 2;
        anchor.current = { fx: (px - r.left) / r.width, fy: (py - r.top) / r.height, x: px - s.left, y: py - s.top };
      }
      latest.current = to;
      setScale(near(to, fit) ? null : to);
    },
    [fit, lowest, highest],
  );
  const zoomBy = useCallback((f: number, x?: number, y?: number) => zoomTo(latest.current * f, x, y), [zoomTo]);
  // Fit and actual size take turns; a small image, already actual size, doubles.
  const toggle = useCallback((x?: number, y?: number) => zoomTo(scale !== null ? null : fit < 1 ? 1 : 2, x, y), [zoomTo, scale, fit]);

  useLayoutEffect(() => {
    const a = anchor.current;
    anchor.current = null;
    const el = stage.current;
    const img = el?.querySelector("img");
    if (!el || !img || !a) return;
    const r = img.getBoundingClientRect();
    const s = el.getBoundingClientRect();
    el.scrollLeft += r.left - s.left + a.fx * r.width - a.x;
    el.scrollTop += r.top - s.top + a.fy * r.height - a.y;
  }, [shownScale]);

  // Ctrl or ⌘ with the wheel zooms, as does a trackpad's pinch (which
  // browsers send as a wheel with Ctrl); the page itself never zooms.
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (natural) zoomBy(Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.002)), e.clientX, e.clientY);
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [zoomBy, natural]);

  // On a touch screen the stage takes every touch itself (touch-action:
  // none), since the browser would otherwise turn a pinch on a zoomed,
  // scrolling image into a pan: one finger pans, two pinch to zoom. A
  // finger that moved does not also tap the backdrop closed.
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number; scale: number } | null>(null);
  const moved = useRef(false);
  const onPointer = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "touch") {
      if (e.type === "pointerdown") moved.current = false;
      return;
    }
    const el = e.currentTarget;
    const last = touches.current.get(e.pointerId);
    if (e.type === "pointerdown") {
      el.setPointerCapture(e.pointerId);
      if (touches.current.size === 0) moved.current = false;
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    } else if (e.type === "pointermove" && last) {
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.current.size === 1) {
        el.scrollLeft -= e.clientX - last.x;
        el.scrollTop -= e.clientY - last.y;
        if (Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y) > 2) moved.current = true;
      }
    } else {
      touches.current.delete(e.pointerId);
    }
    if (touches.current.size !== 2 || !natural) {
      pinch.current = null;
      return;
    }
    moved.current = true;
    const [a, b] = [...touches.current.values()];
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    if (!pinch.current) pinch.current = { d, scale: latest.current };
    else if (d > 0) zoomTo((pinch.current.scale * d) / pinch.current.d, (a.x + b.x) / 2, (a.y + b.y) / 2);
  };

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && at < list.length - 1) setAt(at + 1);
      else if (e.key === "ArrowLeft" && at > 0) setAt(at - 1);
      else if (!natural && ["+", "=", "-", "0", "z", "Z"].includes(e.key)) {
        // Nothing to zoom until the picture is in.
      } else if (e.key === "+" || e.key === "=") zoomBy(step);
      else if (e.key === "-") zoomBy(1 / step);
      else if (e.key === "0") zoomTo(null);
      else if (e.key.toLowerCase() === "z") toggle();
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    addEventListener("keydown", key, true);
    return () => removeEventListener("keydown", key, true);
  }, [at, list.length, onClose, zoomBy, zoomTo, toggle, natural]);

  const percent = Math.round(shownScale * 100);
  return createPortal(
    <div
      className="image-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={t("Image: {name}", { name: shown.file.name })}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <header>
        <div className="image-viewer-title">
          <strong>{shown.file.name}</strong>
          <span>
            {session.name(shown.message.author.id)} · {new Date(shown.message.created_at).toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            {list.length > 1 && " · " + t("{at} of {n}", { at: at + 1, n: list.length })}
          </span>
        </div>
        <div className="image-viewer-zoom" role="group" aria-label={t("Zoom")}>
          <button onClick={() => zoomBy(1 / step)} aria-label={t("Zoom out")} title={t("Zoom out (−)")} disabled={!natural || near(shownScale, lowest)}>
            −
          </button>
          <button
            className="image-viewer-percent"
            onClick={() => toggle()}
            aria-label={t("Zoom: {percent}%", { percent })}
            title={scale !== null ? t("Fit to screen (Z)") : t("Actual size (Z)")}
            disabled={!natural}
          >
            {percent}%
          </button>
          <button onClick={() => zoomBy(step)} aria-label={t("Zoom in")} title={t("Zoom in (+)")} disabled={!natural || near(shownScale, highest)}>
            +
          </button>
        </div>
        {url && (
          <>
            <a className="image-viewer-button" href={url} download={shown.file.name}>
              {t("Download")}
            </a>
            <a className="image-viewer-button" href={url} target="_blank" rel="noopener noreferrer">
              {t("Open original")}
            </a>
          </>
        )}
        <button className="image-viewer-close" onClick={onClose} aria-label={t("Close image")}>
          <IconClose size={20} />
        </button>
      </header>
      <div
        ref={stage}
        className={`image-viewer-stage ${scale !== null && scale > fit ? "zoomed" : ""}`}
        onClick={(e) => e.target === e.currentTarget && !moved.current && onClose()}
        onPointerDown={onPointer}
        onPointerMove={onPointer}
        onPointerUp={onPointer}
        onPointerCancel={onPointer}
      >
        {url ? (
          <img
            src={url}
            alt={shown.file.name}
            style={size ?? undefined}
            onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth || 1, h: e.currentTarget.naturalHeight || 1 })}
            onDoubleClick={(e) => toggle(e.clientX, e.clientY)}
            draggable={false}
          />
        ) : (
          <span className="image-viewer-loading">{t("Loading…")}</span>
        )}
      </div>
      {at > 0 && (
        <button className="image-viewer-nav prev" onClick={() => setAt(at - 1)} aria-label={t("Previous image")}>
          ‹
        </button>
      )}
      {at < list.length - 1 && (
        <button className="image-viewer-nav next" onClick={() => setAt(at + 1)} aria-label={t("Next image")}>
          ›
        </button>
      )}
    </div>,
    document.body,
  );
}
